import { supabase, isConfigured } from './supabase';
import storage from './storage';
import { mergeActivityMaps, mergeActivityLists } from './activityService';

const TABLE = 'crm_team_activities';
const LOCAL_KEY = 'crm_activities';

let _activeActivitiesChannel = null;

function dispatchSyncError(message, detail) {
  try {
    window.dispatchEvent(new CustomEvent('crm-error', {
      detail: { code: 'TEAM_ACTIVITIES_CLOUD', message, detail: String(detail || '') },
    }));
  } catch { /* ignore */ }
}

function broadcastLead(leadId) {
  try {
    const lid = String(leadId);
    window.dispatchEvent(new CustomEvent('crm-activities-changed', { detail: { leadId: lid } }));
    window.dispatchEvent(new CustomEvent('crm-activities-changed', { detail: { leadId: '__all__' } }));
  } catch { /* ignore */ }
}

/**
 * Alle Zeilen aus Supabase laden und mit lokalem crm_activities mergen (Offline + Team).
 */
export async function pullTeamActivitiesIntoLocal() {
  if (!isConfigured) return;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { data, error } = await supabase
    .from(TABLE)
    .select('lead_id, activities')
    .limit(20000);

  if (error) {
    dispatchSyncError('Team-Aktivitäten konnten nicht geladen werden.', error.message);
    throw error;
  }

  const remote = {};
  for (const row of data || []) {
    const lk = String(row.lead_id);
    remote[lk] = Array.isArray(row.activities) ? row.activities : [];
  }

  const raw = storage.get(LOCAL_KEY, {});
  const local = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const merged = mergeActivityMaps(local, remote);
  storage.setLocal(LOCAL_KEY, merged);
}

/**
 * Eine Lead-Zeile in die Team-Tabelle schreiben (nach lokalem Speichern).
 */
export async function syncLeadRowToTeamCloud(leadId, activities) {
  if (!isConfigured) return;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const payload = {
    lead_id: String(leadId),
    activities: Array.isArray(activities) ? activities : [],
    updated_at: new Date().toISOString(),
    updated_by: user.id,
  };

  const { error } = await supabase
    .from(TABLE)
    .upsert(payload, { onConflict: 'lead_id' });

  if (error) {
    dispatchSyncError('Aktivitäten konnten nicht in der Team-Cloud gespeichert werden.', error.message);
  }
}

/** Entfernt eine Lead-Zeile in der Team-Tabelle (z. B. nach Duplikat-Merge). */
export async function deleteTeamActivityRow(leadId) {
  if (!isConfigured) return;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  const { error } = await supabase.from(TABLE).delete().eq('lead_id', String(leadId));
  if (error) dispatchSyncError('Team-Aktivitäten: Zeile konnte nicht gelöscht werden.', error.message);
}

/**
 * Remote-Änderung in localStorage mergen (Realtime / anderer Client).
 */
export function mergeRemoteRowIntoLocal(leadId, remoteActivities) {
  const raw = storage.get(LOCAL_KEY, {});
  const all = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const nk = String(leadId);
  const mergedActs = mergeActivityLists(all[nk] || [], Array.isArray(remoteActivities) ? remoteActivities : []);
  storage.setLocal(LOCAL_KEY, { ...all, [nk]: mergedActs });
  broadcastLead(nk);
}

/**
 * Supabase Realtime: Team-Tabelle abonnieren.
 * @returns {() => void} unsubscribe
 */
export function subscribeTeamActivities() {
  if (!isConfigured) return () => {};

  if (!_activeActivitiesChannel) {
    _activeActivitiesChannel = supabase
      .channel('crm_team_activities_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: TABLE },
        (payload) => {
          const row = payload.new && Object.keys(payload.new).length ? payload.new : null;
          if (!row?.lead_id) return;
          if (payload.eventType === 'DELETE') return;
          mergeRemoteRowIntoLocal(row.lead_id, row.activities);
        }
      )
      .subscribe((status, err) => {
        if (status === 'CHANNEL_ERROR' && err) {
          dispatchSyncError('Echtzeit-Updates für Aktivitäten fehlgeschlagen.', err.message || String(err));
        }
      });
  }

  return () => {
    try {
      supabase.removeChannel(_activeActivitiesChannel);
      _activeActivitiesChannel = null;
    } catch { /* ignore */ }
  };
}
